import { useEffect, useState } from 'react';
import { GRID_ROWS } from '@/pages/home/gameConfig';

export interface ViewportMetrics {
  height: number;
  tileSize: number;
  boardPadding: number;
  guidanceHeight: number;
  sequenceHeight: number;
  screenPt: number;
  screenPb: number;
  sequenceMt: number;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

// 실제 사용 가능한 viewport 높이를 읽어 게임 영역 레이아웃 지표를 계산한다.
// 브라우저 주소창/탭바 등 UI 변화는 visualViewport.height 로 감지한다.
function computeMetrics(): ViewportMetrics {
  const visualViewport =
    typeof window !== 'undefined' ? window.visualViewport : undefined;
  const height = Math.round(
    visualViewport?.height ??
      (typeof window !== 'undefined' ? window.innerHeight : 800),
  );
  const width = typeof window !== 'undefined' ? window.innerWidth : 390;

  // 화면 높이에 따라 상하 여백 / 고정 영역을 우선 축소
  let screenPt: number;
  let screenPb: number;
  let guidanceHeight: number;
  let sequenceHeight: number;
  let sequenceMt: number;
  let boardPadding: number;

  if (height >= 780) {
    screenPt = 20;
    screenPb = 16;
    guidanceHeight = 64;
    sequenceHeight = 44;
    sequenceMt = 8;
    boardPadding = 24;
  } else if (height >= 660) {
    screenPt = 14;
    screenPb = 12;
    guidanceHeight = 56;
    sequenceHeight = 40;
    sequenceMt = 6;
    boardPadding = 16;
  } else if (height >= 560) {
    screenPt = 10;
    screenPb = 8;
    guidanceHeight = 48;
    sequenceHeight = 36;
    sequenceMt = 4;
    boardPadding = 10;
  } else {
    screenPt = 6;
    screenPb = 6;
    guidanceHeight = 40;
    sequenceHeight = 32;
    sequenceMt = 4;
    boardPadding = 6;
  }

  // 고정 소비 높이: 헤더(56) + 상/하 여백 + 순서 상단 마진 + 안내 + 순서 영역
  const fixed =
    56 + screenPt + screenPb + sequenceMt + guidanceHeight + sequenceHeight;
  const boardHeight = height - fixed;
  const maxTileByHeight = Math.floor(
    (boardHeight - boardPadding * 2) / GRID_ROWS,
  );

  // 너비 기반 타일 크기 (기존 17vw, 60~72px 유지)
  const widthTile = clamp(Math.round(width * 0.17), 60, 72);
  // 높이가 부족할 때만 타일을 최후 수단으로 축소 (최소 40px)
  const tileSize = clamp(Math.min(widthTile, maxTileByHeight), 40, 72);

  return {
    height,
    tileSize,
    boardPadding,
    guidanceHeight,
    sequenceHeight,
    screenPt,
    screenPb,
    sequenceMt,
  };
}

// CSS 변수를 문서 루트에 주입해 하위 컴포넌트가 var(--*)로 소비할 수 있게 한다.
function applyMetrics(metrics: ViewportMetrics): void {
  const root = document.documentElement.style;
  root.setProperty('--tile-size', `${metrics.tileSize}px`);
  root.setProperty('--board-padding', `${metrics.boardPadding}px`);
  root.setProperty('--guidance-height', `${metrics.guidanceHeight}px`);
  root.setProperty('--sequence-height', `${metrics.sequenceHeight}px`);
  root.setProperty('--screen-pt', `${metrics.screenPt}px`);
  root.setProperty('--screen-pb', `${metrics.screenPb}px`);
  root.setProperty('--sequence-mt', `${metrics.sequenceMt}px`);
}

export function useViewportMetrics(): ViewportMetrics {
  const [metrics, setMetrics] = useState<ViewportMetrics>(() => computeMetrics());

  useEffect(() => {
    const visualViewport = window.visualViewport;
    const update = () => {
      const next = computeMetrics();
      setMetrics(next);
      applyMetrics(next);
    };

    update();

    visualViewport?.addEventListener('resize', update);
    visualViewport?.addEventListener('scroll', update);
    window.addEventListener('resize', update);
    window.addEventListener('orientationchange', update);

    return () => {
      visualViewport?.removeEventListener('resize', update);
      visualViewport?.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
      window.removeEventListener('orientationchange', update);
    };
  }, []);

  return metrics;
}